import { useTranslation } from "react-i18next"
import { Banner } from "@/components/ui/banner"
import { Tag } from "@/components/ui/tag"
import { BetterDocExchangeLink } from "@/modules/admin/better-doc/components/better-doc-exchange-link"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { REMOTE_REQUEST_STATUS_TAG_VARIANTS } from "@/modules/admin/better-doc/lib/better-doc-tag-variants"
import { isBetterDocTextPresent } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { de_BetterDocRemoteRequestStatus } from "@/types/__generated__/union-types"

type BetterDocRemoteRequestStatusProps = {
  status: de_BetterDocRemoteRequestStatus
  httpStatusCode: number | null
  errorReason: string | null
  exchangeId: number | null
}

export const BetterDocRemoteRequestStatus = ({
  status,
  httpStatusCode,
  errorReason,
  exchangeId,
}: BetterDocRemoteRequestStatusProps) => {
  const { t } = useTranslation()

  const renderProblem = () => {
    switch (status) {
      case "RECEIVED":
        return null
      case "NOT_FOUND":
        return (
          <Banner type="inline" variant="info" data-testid="better-doc-admin-remote-request-problem">
            {t("admin.betterDoc.remoteRequest.notFound")}
          </Banner>
        )
      case "INVALID_RECOMMENDATION_ID":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-remote-request-problem">
            {t("admin.betterDoc.remoteRequest.invalidRecommendationId")}
          </Banner>
        )
      case "REQUEST_FAILED":
      case "UNEXPECTED_RESPONSE":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-remote-request-problem">
            <div className="flex flex-col">
              <span>{t("admin.betterDoc.remoteRequest.failed")}</span>
              {httpStatusCode === null ? null : (
                <span>{t("admin.betterDoc.remoteRequest.httpStatusCode", { httpStatusCode })}</span>
              )}
              {isBetterDocTextPresent(errorReason) ? (
                <span>{t("admin.betterDoc.remoteRequest.errorReason", { errorReason })}</span>
              ) : null}
            </div>
          </Banner>
        )
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="better-doc-admin-remote-request-status">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-on-surface-subdued text-xs">{t("admin.betterDoc.remoteRequest.status")}</span>
        <Tag
          type={REMOTE_REQUEST_STATUS_TAG_VARIANTS[status]}
          label={getBetterDocEnumLabel(t, "remoteRequestStatus", status)}
          data-testid="better-doc-admin-remote-request-status-tag"
        />
        {exchangeId === null ? null : <BetterDocExchangeLink exchangeId={exchangeId} />}
      </div>
      {renderProblem()}
    </div>
  )
}
